import React from 'react';
import { Button } from 'antd';
import { CheckCircleFilled, PlusOutlined } from '@ant-design/icons';
import { ServiceTypeModel } from '@/typings/models/ServiceType';
import styles from './index.less';
import classNames from 'classnames';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import NavLabel from '@/components/NavLabel';

interface ServiceTypeItemProps {
  serviceType: ServiceTypeModel;
  isAdded: boolean;
  onAdd?: (uuid: string) => void;
}

const ServiceTypeItem: React.FC<ServiceTypeItemProps> = ({ serviceType, isAdded, onAdd }) => {
  return (
    <div className={styles.serviceTypeItem}>
      <div className={styles.serviceInfo}>
        {serviceType.icon && <span className={styles.icon}>{serviceType.icon}</span>}
        <span>{serviceType.name}</span>
      </div>
      {isAdded ? (
        <span className={styles.addedLabel}>
          <CheckCircleFilled style={{ marginRight: 4 }} />
          <F id="common.actions.added" defaultMessage="Added" />
        </span>
      ) : (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onAdd?.(serviceType.uuid)}
        >
          <F id={'common.actions.add'}/>
        </Button>
      )}
    </div>
  );
};

interface ServiceTypeListProps {
  availableServiceTypes: ServiceTypeModel[];
  addedServiceTypes: ServiceTypeModel[];
  onAddService: (uuid: string) => void;
  onCreateServiceType: () => void;
}

const ServiceTypeList: React.FC<ServiceTypeListProps> = ({
  availableServiceTypes,
  addedServiceTypes,
  onAddService,
  onCreateServiceType,
}) => {
  return (
    <div className={styles.serviceTypeList}>
      <div className={styles.serviceGroup}>
        {availableServiceTypes?.length > 0 && (
          <NavLabel>
            <F
              id="pages.locations.serviceTypes.available"
              defaultMessage="Available"
            />
          </NavLabel>
        )}
        {availableServiceTypes.map((st) => (
          <ServiceTypeItem
            key={st.uuid}
            serviceType={st}
            isAdded={false}
            onAdd={onAddService}
          />
        ))}
        <Button
          color={'primary'}
          type={'dashed'}
          block
          size={'large'}
          icon={<PlusOutlined />}
          onClick={onCreateServiceType}
        >
          <F entity={INTL_ENTITY.SERVICE_TYPE} action={INTL_ACTION.CREATE} />
        </Button>
      </div>

      {addedServiceTypes.length > 0 && (
        <div className={classNames(styles.serviceGroup, styles.addedServices)}>
          <NavLabel>
            <F
              id="pages.locations.serviceTypes.alreadyAdded"
              defaultMessage="Already Added"
            />
          </NavLabel>
          {addedServiceTypes.map((st) => (
            <ServiceTypeItem key={st.uuid} serviceType={st} isAdded={true} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceTypeList;
